import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import VaulzoLogo from "@/features/landing/VaulzoLogo";
import { useAuth } from "./AuthProvider";

const DEMO_EMAIL = "demo@vaulzo.app";
const DEMO_PASSWORD = "VaulzoDemo2026";

// How long a single click waits for a possible second one before it
// navigates home. Short enough to feel instant, long enough to catch a
// double-click.
const DOUBLE_CLICK_WINDOW_MS = 260;

/**
 * Easter egg: the auth-page logo still links home on a single click,
 * but a quick double-click opens a small card with the demo account's
 * credentials and a one-tap demo sign-in. Deliberately undiscoverable —
 * it exists for walking someone through the product in person.
 */
export default function DemoPeek() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const clickTimer = useRef(null);

  // Cancel a pending navigation if we unmount first.
  useEffect(() => () => clearTimeout(clickTimer.current), []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape" && !busy) setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, busy]);

  const handleClick = () => {
    if (clickTimer.current) {
      // Second click inside the window: it's a double-click.
      clearTimeout(clickTimer.current);
      clickTimer.current = null;
      setError("");
      setOpen(true);
      return;
    }
    clickTimer.current = setTimeout(() => {
      clickTimer.current = null;
      navigate("/");
    }, DOUBLE_CLICK_WINDOW_MS);
  };

  const signInAsDemo = async () => {
    setBusy(true);
    setError("");
    try {
      await login({ email: DEMO_EMAIL, password: DEMO_PASSWORD });
      navigate("/dashboard", { replace: true });
    } catch {
      setError("Demo sign-in failed — try the credentials manually.");
      setBusy(false);
    }
  };

  return (
    <>
      <button type="button" onClick={handleClick} aria-label="Back to home" className="inline-flex">
        <VaulzoLogo />
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[120] flex items-center justify-center bg-vaulzo-ink/30 px-6 animate-vz-fade"
          onMouseDown={() => !busy && setOpen(false)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Demo account"
            onMouseDown={(e) => e.stopPropagation()}
            className="w-full max-w-[360px] rounded-[20px] border border-vaulzo-hairline bg-white p-6 shadow-[0_18px_50px_rgba(23,24,26,0.18)]"
          >
            <VaulzoLogo />
            <p className="mt-5 text-[13px] leading-relaxed text-vaulzo-faint">
              Found the demo. A pre-filled account with every asset type - look around, change things, it resets to real market prices nightly.
            </p>

            <div className="mt-5 space-y-3">
              <CredRow label="Email" value={DEMO_EMAIL} />
              <CredRow label="Password" value={DEMO_PASSWORD} />
            </div>

            {error && (
              <p role="alert" className="mt-4 text-sm text-vaulzo-danger">
                {error}
              </p>
            )}

            <button
              type="button"
              onClick={signInAsDemo}
              disabled={busy}
              className="mt-6 flex h-11 w-full items-center justify-center rounded-xl bg-vaulzo-accent text-[15px] font-semibold text-white transition-colors hover:bg-vaulzo-accent-hover disabled:opacity-60"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : "Sign in as demo"}
            </button>
          </div>
        </div>
      )}
    </>
  );
}

function CredRow({ label, value }) {
  return (
    <div className="flex items-baseline justify-between rounded-xl border border-vaulzo-hairline bg-vaulzo-bg px-4 py-3">
      <span className="text-[11px] uppercase tracking-[0.08em] text-vaulzo-fainter">{label}</span>
      <span className="num select-all text-[13px] font-semibold text-vaulzo-ink">{value}</span>
    </div>
  );
}
